import { isEssentialPersistedTab, isEssentialPersistedTabContent } from 'apiConnector/tabs/tabs.model';
import type { Action } from 'state/actions';
import { addOrPatchKey, patchKey, removeKey } from 'state/utils/collection';
import {
  EssentialWorkspaceState,
  EssentialWorkspaceStateMap,
  initialEssentialWorkSpaceState,
} from 'state/workspaces/essential/model';
export function essentialWorkspacesReducer(
  state: EssentialWorkspaceStateMap = initialEssentialWorkSpaceState,
  action: Action,
): EssentialWorkspaceStateMap {
  switch (action.type) {
    case 'WORKSPACE_PERSISTED_TABS_APPLIED':
      return action.tabs.reduce((oldState, tab) => {
        if (isEssentialPersistedTab(tab)) {
          return addOrPatchKey(
            oldState,
            tab.id,
            () =>
              ({
                quoteId: tab.quoteId,
                isUnderlyingsResultExpanded: tab.isUnderlyingsResultExpanded,
                isPayoffGraphHidden: tab.isPayoffGraphHidden,
                productVariation: tab.productVariation,
              }) as EssentialWorkspaceState,
          );
        }
        return oldState;
      }, state);

    case 'WORKSPACE_TAB_CREATED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return addOrPatchKey(
        state,
        action.id,
        () =>
          ({
            isFetchingTabContent: false,
            isProductValidated: false,
            productVariation: {},
          }) satisfies EssentialWorkspaceState,
      );
    case 'ESSENTIAL_WORKSPACE_TAB_UPDATE_PRODUCT_VARIATION':
      return patchKey(state, action.tabId, _state => ({
        productVariation: action.productVariation,
      }));
    case 'ESSENTIAL_WORKSPACE_TAB_TOGGLE_UNDERLYINGS_RESULT':
      return patchKey(state, action.tabId, _state => ({
        isUnderlyingsResultExpanded: !(_state?.isUnderlyingsResultExpanded ?? false),
      }));
    case 'ESSENTIAL_WORKSPACE_TAB_PAYOFF_GRAPH_EXPANDED':
      return patchKey(state, action.tabId, _state => ({
        isPayoffGraphHidden: !(_state?.isPayoffGraphHidden ?? false),
      }));
    case 'ESSENTIAL_WORKSPACE_SET_QUOTE':
      const patched: Partial<EssentialWorkspaceState> = { quoteId: action.quoteId };
      return addOrPatchKey(state, action.tabId, () => patched);
    case 'WORKSPACE_TAB_CONTENT_FETCHED':
      if (action.workspace !== 'essential') {
        return state;
      }
      if (!isEssentialPersistedTabContent(action.tabContent)) {
        return state;
      }
      const { productLayout } = action.tabContent;
      return addOrPatchKey(state, action.tabId, () => ({
        isProductValidated: true,
        isFetchingTabContent: false,
        productLayout,
      }));

    case 'WORKSPACE_TAB_REMOVED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return removeKey(state, action.id);

    case 'DEFAULT_FORM_FAILED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({ isProductValidated: true, isFetchingTabContent: false }));
    case 'DEFAULT_FORM_RECEIVED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({
        isProductValidated: true,
        isFetchingTabContent: false,
        productLayout: action.productLayout,
      }));
    case 'FORM_ENRICHMENT_RECEIVED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({
        isProductValidated: true,
        isFetchingTabContent: false,
        productLayout: action.productLayout,
      }));
    case 'WORKSPACE_TAB_CONTENT_FETCH_REQUESTED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({ isFetchingTabContent: true }));
    case 'WORKSPACE_TAB_CONTENT_FETCH_FAILED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({
        isProductValidated: action.tabType === 'reprice',
        isFetchingTabContent: false,
      }));
    case 'WORKSPACE_RESET_TAB_REQUESTED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        productLayout: undefined,
      }));
    case 'WORKSPACE_TAB_CLONED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return addOrPatchKey(state, action.newTabId, () => ({
        isProductValidated: true,
        isFetchingTabContent: true,
        productVariation: {
          productType: action.productType,
          productSubtype: action.productSubtype,
          wrapper: action.wrapper,
        },
      }));
    case 'WORKSPACE_TAB_PRODUCT_SELECTION_SUBMITTED':
      if (action.workspaceType !== 'essential') {
        return state;
      }
      return addOrPatchKey(
        state,
        action.tabId,
        () =>
          ({
            isProductValidated: true,
            isFetchingTabContent: true,
            productVariation: {
              productType: action.productVariation.productType,
              productSubtype: action.productVariation.productSubType,
              wrapper: action.productVariation.wrapper,
            },
          }) satisfies EssentialWorkspaceState,
      );
    case 'WORKSPACE_QUOTE_RELOADED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return addOrPatchKey(state, action.temporaryFormId, () => ({
        isProductValidated: true,
        isFetchingTabContent: true,
        productVariation: action.productVariation,
      }));

    default:
      return state;
  }
}
